import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Heart, Repeat, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { createDonationCheckout } from "../lib/api";

const PRESETS = {
  one_time: [50, 100, 250, 1000],
  monthly: [25, 50, 100, 250],
};

export const DonationModal = ({ open, onClose, defaultFrequency = "one_time", defaultAmount = null }) => {
  const [frequency, setFrequency] = useState(defaultFrequency);
  const [amount, setAmount] = useState(defaultAmount || 100);
  const [custom, setCustom] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [publicOptIn, setPublicOptIn] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setFrequency(defaultFrequency);
      setAmount(defaultAmount || PRESETS[defaultFrequency][1]);
      setCustom(false);
      setPublicOptIn(false);
      setDisplayName("");
      setLoading(false);
    }
  }, [open, defaultFrequency, defaultAmount]);

  const startCheckout = async () => {
    const amt = Number(amount);
    if (!amt || amt < 1) { toast.error("Please enter a donation amount of at least $1."); return; }
    if (amt > 50000) { toast.error("For gifts over $50,000, please contact us directly."); return; }
    setLoading(true);
    try {
      const res = await createDonationCheckout({
        amount: amt, frequency, origin_url: window.location.origin, name, email,
        public: publicOptIn, display_name: displayName || name,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="donation-modal"
        >
          <div className="absolute inset-0 bg-charcoal/50 backdrop-blur-sm" onClick={loading ? undefined : onClose} />
          <motion.div
            className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
            initial={{ y: 24, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center justify-between px-6 py-5 bg-navy text-offwhite">
              <div className="flex items-center gap-2.5">
                <Heart className="h-5 w-5 text-gold" />
                <span className="font-heading text-lg font-bold">Support a veteran's reset</span>
              </div>
              <button onClick={onClose} disabled={loading} data-testid="donation-close" aria-label="Close" className="h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-white/10 transition-colors disabled:opacity-40">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 md:p-7">
              <div className="grid grid-cols-2 gap-2 p-1 bg-offwhite rounded-lg border border-softgray">
                {[
                  { k: "one_time", label: "One-time", icon: Heart },
                  { k: "monthly", label: "Monthly", icon: Repeat },
                ].map((f) => (
                  <button
                    key={f.k}
                    onClick={() => { setFrequency(f.k); setCustom(false); setAmount(PRESETS[f.k][1]); }}
                    data-testid={`donation-freq-${f.k}`}
                    className={`flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-semibold transition-colors ${
                      frequency === f.k ? "bg-navy text-offwhite" : "text-charcoal hover:text-navy"
                    }`}
                  >
                    <f.icon className="h-4 w-4" /> {f.label}
                  </button>
                ))}
              </div>

              <div className="mt-6">
                <div className="label-caps text-navy mb-3">Choose an amount</div>
                <div className="grid grid-cols-4 gap-2">
                  {PRESETS[frequency].map((p) => (
                    <button
                      key={p}
                      onClick={() => { setAmount(p); setCustom(false); }}
                      data-testid={`donation-amount-${p}`}
                      className={`py-3 rounded-md border text-sm font-bold transition-colors ${
                        !custom && Number(amount) === p ? "border-navy bg-navy text-offwhite" : "border-softgray bg-white text-charcoal hover:border-navy"
                      }`}
                    >
                      ${p}
                    </button>
                  ))}
                </div>
                <div className="mt-3 relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate font-semibold">$</span>
                  <input
                    type="number" min="1"
                    value={custom ? amount : ""}
                    onFocus={() => setCustom(true)}
                    onChange={(e) => { setCustom(true); setAmount(e.target.value); }}
                    placeholder="Other amount"
                    data-testid="donation-custom-amount"
                    className="w-full rounded-md border border-softgray bg-white pl-8 pr-4 py-3 text-charcoal placeholder:text-slate/70 focus:border-navy focus:outline-none focus:ring-2 focus:ring-navy/30"
                  />
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" data-testid="donation-name" className="rounded-md border border-softgray bg-white px-4 py-3 text-sm text-charcoal placeholder:text-slate/70 focus:border-navy focus:outline-none focus:ring-2 focus:ring-navy/30" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optional)" data-testid="donation-email" className="rounded-md border border-softgray bg-white px-4 py-3 text-sm text-charcoal placeholder:text-slate/70 focus:border-navy focus:outline-none focus:ring-2 focus:ring-navy/30" />
              </div>

              <label className="mt-4 flex items-start gap-3 cursor-pointer select-none" data-testid="donation-public-optin">
                <input type="checkbox" checked={publicOptIn} onChange={(e) => setPublicOptIn(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-softgray text-navy focus:ring-navy/30" />
                <span className="text-sm text-charcoal/80 leading-snug">Add my name to the public supporters wall.</span>
              </label>
              {publicOptIn && (
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Display name for the wall (e.g. The Smith Family, or Anonymous)"
                  data-testid="donation-display-name"
                  className="mt-3 w-full rounded-md border border-softgray bg-white px-4 py-3 text-sm text-charcoal placeholder:text-slate/70 focus:border-navy focus:outline-none focus:ring-2 focus:ring-navy/30"
                />
              )}

              <button
                onClick={startCheckout}
                disabled={loading}
                data-testid="donation-submit"
                className="group mt-6 w-full inline-flex items-center justify-center gap-2 bg-gold text-charcoal px-7 py-4 rounded-md text-sm font-bold uppercase tracking-wide hover:bg-gold-deep transition-colors disabled:opacity-60"
              >
                {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Redirecting to secure checkout…</>) : (<><ShieldCheck className="h-4 w-4" /> Donate ${Number(amount) || 0}{frequency === "monthly" ? "/mo" : ""}</>)}
              </button>
              <p className="mt-3 text-center text-xs text-slate">Secure checkout by Stripe. Test card: 4242 4242 4242 4242.</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DonationModal;
